/**
 * jai1 tasks update <id> [-s <status>] [-n <notes>] [-j]
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import { STATUS_ICONS, type TaskStatusType } from '../../types/task.types.js';

const VALID_STATUSES = ['todo', 'in_progress', 'done', 'cancelled'];

interface UpdateOptions {
    status?: string;
    notes?: string;
    json?: boolean;
}

export function createTaskUpdateCommand(): Command {
    return new Command('update')
        .description('Update task status and/or notes')
        .argument('<id>', 'Task ID (e.g. T-001)')
        .option('-s, --status <status>', 'New status: todo, in_progress, done, cancelled')
        .option('-n, --notes <notes>', 'Task notes (e.g. "files: a.ts, b.ts")')
        .option('-j, --json', 'Output JSON')
        .action(async (id: string, options: UpdateOptions) => {
            if (!options.status && !options.notes) {
                console.error(chalk.red('❌ At least one of --status or --notes is required'));
                process.exit(1);
            }

            if (options.status && !VALID_STATUSES.includes(options.status)) {
                console.error(chalk.red(`❌ Invalid status. Must be: ${VALID_STATUSES.join(', ')}`));
                process.exit(1);
            }

            const service = new TaskService();

            try {
                // Warn if setting to in_progress on a blocked task
                if (options.status === 'in_progress') {
                    const existingTask = await service.findById(id);
                    if (existingTask) {
                        const { blocked, blockedBy } = await service.isBlocked(existingTask);
                        if (blocked) {
                            console.log(chalk.yellow(`⚠️  Task ${id} is blocked by: ${blockedBy.join(', ')}`));
                            console.log(chalk.yellow(`   Dependencies chưa done. Tiếp tục update...`));
                        }
                    }
                }

                const changes: Record<string, string> = {};
                if (options.status) changes.status = options.status;
                if (options.notes) changes.notes = options.notes;

                const task = await service.update(id, changes);

                if (options.json) {
                    console.log(JSON.stringify(task, null, 2));
                    return;
                }

                const parts: string[] = [];
                if (options.status) {
                    const icon = STATUS_ICONS[task.status as TaskStatusType] || '📋';
                    parts.push(`${icon} ${task.status}`);
                }
                if (options.notes) {
                    parts.push(`📝 notes updated`);
                }
                console.log(chalk.green(`✅ ${task.id} → ${parts.join(' | ')}`));
            } catch (error) {
                console.error(chalk.red(`❌ ${error instanceof Error ? error.message : String(error)}`));
                process.exit(1);
            }
        });
}
